import { useEffect, useRef } from 'react';

/**
 * useScrollAutoplay
 * ---------------------------------------------------------------------------
 * Pauses a <video> element the moment it scrolls out of the viewport and
 * resumes it once it scrolls back in — so off-screen frames stop burning
 * bandwidth/CPU. Resuming only happens if `isPlaying` (the caller's play
 * intent) is true, so a video the user manually paused stays paused even
 * after it re-enters view.
 *
 * @param {React.RefObject<HTMLVideoElement>} videoRef
 * @param {boolean} isPlaying - current play intent (true = should be playing)
 * @param {number} threshold - fraction of the video that must be visible
 * @param {boolean} isMounted - whether the <video> is currently rendered.
 *   Pass the same condition used to conditionally render the <video> (e.g.
 *   `uploadedMedia && mediaType === 'video'`) so the observer re-attaches
 *   correctly when a video mounts AFTER the component's initial render
 *   (reading a ref straight off the deps array misses that transition).
 */
const useScrollAutoplay = (videoRef, isPlaying, threshold = 0.25, isMounted = true) => {
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (isPlayingRef.current) {
            video.play().catch(() => {});
          }
        } else {
          video.pause();
        }
      },
      { threshold }
    );

    observer.observe(video);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, threshold]);
};

export default useScrollAutoplay;
